import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsLink } from '@frontend/settings-routing';
import { SettingsGroup } from '@frontend/settings-ui';
import { IndexRow, Text, TextLink, Heading, useModalControl } from '@frontend/design-system';
import { AiInstructionInfoModal } from '../components/settings-v2/common/ai-instruction-info-modal';
import { AiReceptionistSettingsRoutes } from '../constants';
import { AiReceptionistTrackingIds } from '../tracking-ids';

export const AiReceptionistPageV2 = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { modalProps, openModal } = useModalControl();

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Heading level={3} css={styles.heading}>
          {t('AI Instructions & Knowledge Base')}
        </Heading>
        <TextLink
          trackingId={AiReceptionistTrackingIds.infoModalOpenLink}
          onClick={(e) => {
            e.preventDefault();
            openModal();
          }}
          css={styles.link}
        >
          <Icon name='info-small' />
          <span>{t('What is this information used for?')}</span>
        </TextLink>
      </div>

      <SettingsGroup title={t('Workflow Instructions')}>
        <IndexRow>
          <IndexRow.Item
            as={SettingsLink}
            to={AiReceptionistSettingsRoutes.INSTRUCTIONS_MESSAGING_VOICE}
            trackingId={AiReceptionistTrackingIds.instructionsMessagingVoiceLink}
            css={styles.itemRow}
          >
            <IndexRow.Item.Head>
              <Icon name='aiassistant-small' />
            </IndexRow.Item.Head>
            <IndexRow.Item.Title>{t('Messaging & Voice Instruction')}</IndexRow.Item.Title>
          </IndexRow.Item>
          <IndexRow.Item
            as={SettingsLink}
            to={AiReceptionistSettingsRoutes.INSTRUCTIONS_SCHEDULING}
            trackingId={AiReceptionistTrackingIds.instructionsSchedulingLink}
            css={styles.itemRow}
          >
            <IndexRow.Item.Head>
              <Icon name='aiassistant-small' />
            </IndexRow.Item.Head>
            <IndexRow.Item.Title>{t('Scheduling Instruction')}</IndexRow.Item.Title>
          </IndexRow.Item>
        </IndexRow>
      </SettingsGroup>

      <SettingsGroup title={t('Knowledge Base')}>
        <IndexRow>
          <IndexRow.Item
            as={SettingsLink}
            to={AiReceptionistSettingsRoutes.KNOWLEDGE_BASE_BUSINESS}
            trackingId={AiReceptionistTrackingIds.knowledgeBaseBusinessLink}
            css={styles.itemRow}
          >
            <IndexRow.Item.Head>
              <Icon name='note-small' />
            </IndexRow.Item.Head>
            <IndexRow.Item.Title>{t('Business Knowledge')}</IndexRow.Item.Title>
          </IndexRow.Item>
          <IndexRow.Item
            as={SettingsLink}
            to={AiReceptionistSettingsRoutes.KNOWLEDGE_BASE_PAYMENTS}
            trackingId={AiReceptionistTrackingIds.knowledgeBasePaymentsLink}
            css={styles.itemRow}
          >
            <IndexRow.Item.Head>
              <Icon name='note-small' />
            </IndexRow.Item.Head>
            <IndexRow.Item.Title>{t('Payment & Insurance Knowledge')}</IndexRow.Item.Title>
          </IndexRow.Item>
          <IndexRow.Item
            as={SettingsLink}
            to={AiReceptionistSettingsRoutes.KNOWLEDGE_BASE_SCHEDULE}
            trackingId={AiReceptionistTrackingIds.knowledgeBaseScheduleLink}
            css={styles.itemRow}
          >
            <IndexRow.Item.Head>
              <Icon name='note-small' />
            </IndexRow.Item.Head>
            <IndexRow.Item.Title>{t('Schedule & Service Knowledge')}</IndexRow.Item.Title>
          </IndexRow.Item>
          <IndexRow.Item
            as={SettingsLink}
            to={AiReceptionistSettingsRoutes.KNOWLEDGE_BASE_CUSTOM}
            trackingId={AiReceptionistTrackingIds.knowledgeBaseCustomLink}
            css={styles.itemRow}
          >
            <IndexRow.Item.Head>
              <Icon name='note-small' />
            </IndexRow.Item.Head>
            <IndexRow.Item.Title>{t('Custom Knowledge')}</IndexRow.Item.Title>
          </IndexRow.Item>
        </IndexRow>
      </SettingsGroup>

      <Text color='light' size='small'>
        {t('Looking for AI Receptionist event notifications?')}{' '}
        <SettingsLink
          to='/personal/notifications/ai-receptionist'
          trackingId={AiReceptionistTrackingIds.personalNotificationsLink}
        >
          {t('Go to Personal Settings')}
        </SettingsLink>
      </Text>

      <AiInstructionInfoModal {...modalProps} />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    max-width: 700px;
  `,
  itemRow: css`
    .index-row-item-content {
      gap: var(--fabric-space-gap-xs);
    }
  `,
  header: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    margin-bottom: var(--fabric-space-gap-md);
  `,
  heading: css`
    margin: 0;
  `,
  link: css`
    display: inline-flex;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
    width: fit-content;
  `,
};
